import React, { useState, useEffect } from 'react';
import { auth, db } from './firebase';
import { collection, query, where, onSnapshot, doc, updateDoc, orderBy, deleteDoc, getDocs } from './firestore-compat';
import { isSuperUser } from './utils/roleUtils';
import { toTitleCase } from './utils/formatters';

export default function PortalTI() {
  const [profissionais, setProfissionais] = useState([]);
  const [loading, setLoading] = useState(true);

  // Form states for creating users
  const [activeFormId, setActiveFormId] = useState(null);
  const [usuarioIW, setUsuarioIW] = useState('');
  const [senhaIW, setSenhaIW] = useState('');
  const [servidorIW, setServidorIW] = useState('');
  const [usuarioAulas, setUsuarioAulas] = useState('');
  const [senhaAulas, setSenhaAulas] = useState('');
  const [activeTab, setActiveTab] = useState('acessos'); // 'acessos' | 'resets'
  const [activeListTab, setActiveListTab] = useState('pendentes'); // 'pendentes' | 'concluidos'
  const [resets, setResets] = useState([]);

  // Filtros
  const [filterNomeTI, setFilterNomeTI] = useState('');
  const [filterEspTI, setFilterEspTI] = useState('');
  const [filterCoopTI, setFilterCoopTI] = useState('');

  // Modal de Edição (Para TI editar dados da Cooperativa)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingProf, setEditingProf] = useState(null);
  const [editForm, setEditForm] = useState({});

  useEffect(() => {
    // A TI acompanha os que estão em sua fila ou já foram criados, desde que não sejam reprovados/bloqueados antes
    // Na verdade, para KPIs precisos, vamos puxar tudo que o RH aprovou (alguns podem ter sido bloqueados depois)
    const q = query(
      collection(db, 'profissionais_parceiros'), 
      where('statusRH', 'in', ['Aprovado', 'Bloqueado']), // Pode ter bloqueado após aprovação
      orderBy('criadoEm', 'desc')
    );
    const unsub = onSnapshot(q, snap => {
      setProfissionais(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    const q = query(collection(db, 'solicitacoes_ti'), orderBy('criadoEm', 'desc'));
    const unsub = onSnapshot(q, snap => {
      setResets(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return () => unsub();
  }, []);

  const handleEditProf = (p) => {
    setEditingProf(p);
    setEditForm({ ...p });
    setIsEditModalOpen(true);
  };

  const formatBRDate = (isoString) => {
    if (!isoString) return '';
    return new Date(isoString).toLocaleDateString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    if (!window.confirm("Confirmar alterações no cadastro deste profissional?")) return;
    try {
      const dataToSave = {
        ...editForm,
        nome: toTitleCase(editForm.nome)
      };
      await updateDoc(doc(db, 'profissionais_parceiros', editingProf.id), dataToSave);

      // As escalas já lançadas guardam uma cópia da cooperativa do profissional
      // no momento em que o turno foi atribuído — corrigir o cadastro aqui não
      // atualiza sozinho os turnos já salvos. Propaga a cooperativa corrigida
      // para as escalas existentes desse profissional (por nome).
      if (dataToSave.cooperativa !== editingProf.cooperativa) {
        try {
          const escalasSnap = await getDocs(query(
            collection(db, 'escalas_homecare'),
            where('tecnico', '==', editingProf.nome)
          ));
          await Promise.all(escalasSnap.docs.map(d => updateDoc(d.ref, { cooperativa: dataToSave.cooperativa })));
        } catch (syncErr) {
          console.warn('[PortalTI] Erro ao sincronizar cooperativa nas escalas:', syncErr.message);
        }
      }

      setIsEditModalOpen(false);
      setEditingProf(null);
      alert("Cadastro atualizado com sucesso!");
    } catch (err) {
      console.error(err);
      alert("Erro ao atualizar cadastro.");
    }
  };

  const handleFinalizarCadastro = async (p) => {
    if (!usuarioIW || !senhaIW || !usuarioAulas || !senhaAulas || !servidorIW) {
      return alert("Preencha todos os campos, incluindo o Servidor IW!");
    }
    if (!window.confirm(`Liberar acessos para ${p.nome}? A cooperativa será notificada.`)) return;

    try {
      await updateDoc(doc(db, 'profissionais_parceiros', p.id), {
        statusTI: 'Criado',
        usuarioSistema: usuarioIW,
        senhaSistema: senhaIW,
        servidorIW: servidorIW,
        usuarioAulas: usuarioAulas,
        senhaAulas: senhaAulas
      });
      setActiveFormId(null);
      setUsuarioIW('');
      setSenhaIW('');
      setServidorIW('');
      setUsuarioAulas('');
      setSenhaAulas('');
    } catch (err) {
      console.error(err);
      alert('Erro ao finalizar cadastro.');
    }
  };

  const handleToggleBlock = async (p) => {
    const isBlocked = p.statusTI === 'Bloqueado';
    const acao = isBlocked ? 'desbloquear' : 'bloquear';
    if (!window.confirm(`Tem certeza que deseja ${acao} o acesso de ${p.nome}?`)) return;

    try {
      await updateDoc(doc(db, 'profissionais_parceiros', p.id), {
        statusTI: isBlocked ? 'Pendente' : 'Bloqueado'
      });
    } catch (err) {
      console.error(err);
      alert(`Erro ao ${acao} profissional.`);
    }
  };

  const handleDelete = async (p) => {
    if (!window.confirm(`⚠️ ATENÇÃO: Deseja excluir PERMANENTEMENTE o registro de ${p.nome}?\n\nEsta ação não pode ser desfeita e o profissional sumirá de todos os painéis.`)) return;

    try {
      await deleteDoc(doc(db, 'profissionais_parceiros', p.id));
      alert('Profissional removido com sucesso.');
    } catch (err) {
      console.error(err);
      alert('Erro ao excluir profissional.');
    }
  };

  const handleResponderReset = async (r, status) => {
    if (!window.confirm(`Mudar status do reset para: ${status}?`)) return;
    try {
      await updateDoc(doc(db, 'solicitacoes_ti', r.id), { status });
      alert("Solicitação atualizada.");
    } catch (err) {
      console.error(err);
      alert("Erro ao atualizar.");
    }
  };

  const s = {
    page: { padding: '24px', fontFamily: "'Inter', sans-serif", background: '#f8fafc', minHeight: '100vh', color: '#1e293b' },
    header: { marginBottom: '24px' },
    title: { margin: 0, fontSize: '1.8rem', fontWeight: 800, color: '#0f172a' },
    tabs: { display: 'flex', gap: '8px', marginBottom: '24px', borderBottom: '1px solid #e2e8f0', paddingBottom: '8px' },
    tab: (active) => ({ padding: '8px 16px', cursor: 'pointer', fontWeight: 600, borderBottom: active ? '3px solid #3b82f6' : '3px solid transparent', color: active ? '#3b82f6' : '#64748b' }),
    card: { background: '#fff', borderRadius: '12px', padding: '24px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' },
    table: { width: '100%', borderCollapse: 'collapse', marginTop: '16px' },
    th: { textAlign: 'left', padding: '12px', borderBottom: '2px solid #e2e8f0', color: '#64748b', fontSize: '0.85rem' },
    td: { padding: '12px', borderBottom: '1px solid #e2e8f0', fontSize: '0.9rem', verticalAlign: 'top' },
    btn: { background: '#3b82f6', color: '#fff', border: 'none', padding: '8px 16px', borderRadius: '6px', cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem' },
    input: { padding: '8px', border: '1px solid #cbd5e1', borderRadius: '6px', marginBottom: '8px', width: '100%', boxSizing: 'border-box' },
    badge: (status) => ({ padding: '4px 8px', borderRadius: '12px', fontSize: '0.75rem', fontWeight: 700,
      background: status === 'Criado' ? '#dcfce7' : status === 'Bloqueado' ? '#fee2e2' : '#fef9c3',
      color: status === 'Criado' ? '#166534' : status === 'Bloqueado' ? '#991b1b' : '#854d0e',
      display: 'inline-block', marginBottom: '8px'
    }),
    profileBox: { background: '#f1f5f9', padding: '12px', borderRadius: '8px', fontSize: '0.85rem' },
    kpiContainer: { display: 'flex', gap: '16px', marginBottom: '24px' },
    kpiCard: { flex: 1, background: '#fff', padding: '16px', borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' },
    kpiValue: { fontSize: '2rem', fontWeight: 800, margin: '8px 0 0', color: '#0f172a' },
    kpiLabel: { fontSize: '0.85rem', color: '#64748b', fontWeight: 600, textTransform: 'uppercase' }
  };

  // KPIs
  // TI só visualiza o que está com ela (Aprovado pelo RH) ou os Bloqueados pós-aprovação
  const countFila = profissionais.filter(p => p.statusRH === 'Aprovado' && p.statusTI !== 'Criado').length;
  const countAcessos = profissionais.filter(p => p.statusTI === 'Criado').length;
  // Unificar soma para evitar divergência (ignora bloqueados que não chegaram a ter acesso)
  const countTotal = countFila + countAcessos;

  // Agrupamento por Especialidade
  const especialidades = profissionais.reduce((acc, p) => {
    const esp = p.tipo || 'Não inf.';
    acc[esp] = (acc[esp] || 0) + 1;
    return acc;
  }, {});

  // Filtrar apenas o que a TI atende ativamente na tabela
  // Lista de Bloqueados vai aparecer como "Desativado"
  const listaAtiva = profissionais.filter(p => p.statusRH === 'Aprovado' || p.statusTI === 'Bloqueado');

  // Função de ordenação: não-técnicos primeiro, depois técnicos; dentro de cada grupo, mais antigo primeiro
  const sortByEspecialidadeEData = (lista) => {
    return [...lista].sort((a, b) => {
      const isTecnicoA = (a.tipo || '').toUpperCase().includes('TÉCNICO') || (a.tipo || '').toUpperCase().includes('TECNICO');
      const isTecnicoB = (b.tipo || '').toUpperCase().includes('TÉCNICO') || (b.tipo || '').toUpperCase().includes('TECNICO');
      if (isTecnicoA !== isTecnicoB) return isTecnicoA ? 1 : -1; // não-técnico primeiro
      // mesmo grupo → mais antigo primeiro
      const dateA = new Date(a.criadoEm || 0);
      const dateB = new Date(b.criadoEm || 0);
      return dateA - dateB;
    });
  };

  const listaPendentes = sortByEspecialidadeEData(
    listaAtiva.filter(p => p.statusTI !== 'Criado')
  );
  const listaConcluidos = sortByEspecialidadeEData(
    listaAtiva.filter(p => p.statusTI === 'Criado')
  );
  const especialidadesTI = [...new Set(profissionais.map(p => p.tipo).filter(Boolean))].sort();
  const cooperativasTI = [...new Set(profissionais.map(p => p.cooperativa).filter(Boolean))].sort();

  const listaExibida = (activeListTab === 'pendentes' ? listaPendentes : listaConcluidos).filter(p => {
    const matchNome = !filterNomeTI || (p.nome || '').toLowerCase().includes(filterNomeTI.toLowerCase());
    const matchEsp = !filterEspTI || p.tipo === filterEspTI;
    const matchCoop = !filterCoopTI || p.cooperativa === filterCoopTI;
    return matchNome && matchEsp && matchCoop;
  });

  return (
    <div style={s.page}>
      <div style={s.header}>
        <h1 style={s.title}>💻 Criação de Acessos (TI)</h1>
        <p style={{ color: '#64748b', margin: '4px 0 0' }}>Gere os acessos do Sistema IW e da Plataforma de Aulas para os profissionais aprovados.</p>
      </div>

      <div style={s.tabs}>
        <div style={s.tab(activeTab === 'acessos')} onClick={() => setActiveTab('acessos')}>Geração de Acessos</div>
        <div style={s.tab(activeTab === 'resets')} onClick={() => setActiveTab('resets')}>Solicitações de Reset {resets.filter(r=>r.status==='Pendente').length > 0 && `(${resets.filter(r=>r.status==='Pendente').length})`}</div>
      </div>

      <div style={s.kpiContainer}>
        <div style={s.kpiCard}>
          <div style={s.kpiLabel}>Aguardando Criação de Usuário</div>
          <div style={s.kpiValue}>{countFila}</div>
        </div>
        <div style={s.kpiCard}>
          <div style={s.kpiLabel}>Usuários Criados (Ativos)</div>
          <div style={{...s.kpiValue, color: '#16a34a'}}>{countAcessos}</div>
        </div>
        <div style={s.kpiCard}>
          <div style={s.kpiLabel}>Total de Usuários (TI)</div>
          <div style={{...s.kpiValue, color: '#3b82f6'}}>{countTotal}</div>
        </div>
        <div style={{...s.kpiCard, flex: '1.5'}}>
          <div style={s.kpiLabel}>Usuários por Especialidade</div>
          <div style={{ marginTop: '10px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', maxHeight: '60px', overflowY: 'auto' }}>
            {Object.entries(especialidades).sort((a,b) => b[1] - a[1]).map(([name, count]) => (
              <div key={name} style={{ fontSize: '0.8rem', display: 'flex', justifyContent: 'space-between', paddingRight: '15px' }}>
                <span style={{ fontWeight: 600, color: '#475569' }}>{name}:</span>
                <span style={{ fontWeight: 800, color: '#0f172a' }}>{count}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div style={s.card}>
        {activeTab === 'acessos' && (
          loading ? <p>Carregando...</p> : (
            <div>
              {/* Filtros */}
              <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', flexWrap: 'wrap', background: '#f8fafc', padding: '14px', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
                <input
                  type="text"
                  placeholder="🔍 Nome do profissional..."
                  value={filterNomeTI}
                  onChange={e => setFilterNomeTI(e.target.value)}
                  style={{ flex: '1 1 200px', padding: '9px 14px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.88rem', outline: 'none' }}
                />
                <select
                  value={filterEspTI}
                  onChange={e => setFilterEspTI(e.target.value)}
                  style={{ flex: '1 1 180px', padding: '9px 14px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.88rem', outline: 'none', background: '#fff' }}
                >
                  <option value="">Todas especialidades</option>
                  {especialidadesTI.map(e => <option key={e} value={e}>{e}</option>)}
                </select>
                <select
                  value={filterCoopTI}
                  onChange={e => setFilterCoopTI(e.target.value)}
                  style={{ flex: '1 1 180px', padding: '9px 14px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.88rem', outline: 'none', background: '#fff' }}
                >
                  <option value="">Todas cooperativas</option>
                  {cooperativasTI.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
                {(filterNomeTI || filterEspTI || filterCoopTI) && (
                  <button
                    onClick={() => { setFilterNomeTI(''); setFilterEspTI(''); setFilterCoopTI(''); }}
                    style={{ padding: '9px 14px', borderRadius: '8px', border: '1px solid #fca5a5', background: '#fee2e2', color: '#991b1b', fontWeight: 700, fontSize: '0.82rem', cursor: 'pointer', flex: 'none' }}
                  >
                    ✕ Limpar
                  </button>
                )}
              </div>

              {/* Subtabs Pendentes / Concluídos */}
              <div style={{ display: 'flex', gap: '8px', marginBottom: '20px' }}>
                <button
                  onClick={() => setActiveListTab('pendentes')}
                  style={{
                    padding: '10px 20px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: '0.85rem',
                    background: activeListTab === 'pendentes' ? '#fef9c3' : '#f1f5f9',
                    color: activeListTab === 'pendentes' ? '#854d0e' : '#64748b',
                    borderBottom: activeListTab === 'pendentes' ? '3px solid #f59e0b' : '3px solid transparent'
                  }}
                >
                  ⏳ Pendentes ({listaPendentes.length})
                </button>
                <button
                  onClick={() => setActiveListTab('concluidos')}
                  style={{
                    padding: '10px 20px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: '0.85rem',
                    background: activeListTab === 'concluidos' ? '#dcfce7' : '#f1f5f9',
                    color: activeListTab === 'concluidos' ? '#166534' : '#64748b',
                    borderBottom: activeListTab === 'concluidos' ? '3px solid #16a34a' : '3px solid transparent'
                  }}
                >
                  ✅ Concluídos ({listaConcluidos.length})
                </button>
              </div>
            <table style={s.table}>
              <thead>
                <tr>
                  <th style={s.th}>Ficha do Profissional</th>
                  <th style={s.th}>Cooperativa</th>
                  <th style={s.th}>Status TI</th>
                  <th style={{...s.th, width: '350px'}}>Geração de Acessos</th>
                </tr>
              </thead>
              <tbody>
                {listaExibida.map(p => (
                  <tr key={p.id} style={{opacity: p.statusTI === 'Bloqueado' ? 0.6 : 1}}>
                    <td style={s.td}>
                      <div style={s.profileBox}>
                        <strong style={{fontSize: '1rem', color: '#0f172a'}}>{p.nome}</strong><br/>
                        <span style={{color: '#3b82f6', fontWeight: 600}}>{p.tipo} {p.isCoringa ? '🃏' : ''}</span><br/>
                        <strong>CPF:</strong> {p.cpf || 'Não inf.'} | <strong>RG:</strong> {p.rg || 'Não inf.'}<br/>
                        <strong>Conselho:</strong> {p.conselho || 'Não inf.'} | <strong>Registro:</strong> {p.crmCoren || 'Não inf.'}<br/>
                        <strong>Contato:</strong> {p.telefone}<br/>
                        <strong>E-mail:</strong> {p.emailCooperado || 'Não inf.'}
                        <div style={{marginTop: '8px', fontSize: '0.75rem', color: '#475569', background: '#e2e8f0', padding: '6px', borderRadius: '4px'}}>
                          <strong>Solicitado (Coop):</strong> {formatBRDate(p.criadoEm)}
                          {p.aprovadoEm && <div><strong>Aprovado (RH):</strong> {formatBRDate(p.aprovadoEm)}</div>}
                        </div>
                        <button 
                          onClick={() => handleEditProf(p)}
                          style={{
                            marginTop: '10px', width: '100%', padding: '6px', borderRadius: '6px', 
                            border: '1px solid #3b82f6', color: '#3b82f6', background: '#eff6ff', 
                            fontSize: '0.75rem', fontWeight: 700, cursor: 'pointer'
                          }}
                        >
                          ✏️ Editar Cadastro (TI)
                        </button>
                      </div>
                    </td>
                    <td style={s.td}>{p.cooperativa}</td>
                    <td style={s.td}>
                      <span style={s.badge(p.statusTI)}>{p.statusTI}</span>
                      <button 
                        onClick={() => handleToggleBlock(p)}
                        style={{background: 'transparent', border: 'none', color: p.statusTI === 'Bloqueado' ? '#16a34a' : '#dc2626', textDecoration: 'underline', padding: '4px 0', fontSize: '0.75rem', cursor: 'pointer', display: 'block', marginTop: '8px'}}
                      >
                        {p.statusTI === 'Bloqueado' ? '✔️ Desbloquear' : '🚫 Sinalizar Bloqueio'}
                      </button>

                      {isSuperUser(auth.currentUser?.email) && (
                        <button 
                          onClick={() => handleDelete(p)}
                          style={{background: 'transparent', border: 'none', color: '#dc2626', textDecoration: 'underline', padding: '4px 0', fontSize: '0.75rem', cursor: 'pointer', display: 'block', marginTop: '4px', fontWeight: 'bold'}}
                        >
                          🗑️ Excluir Permanente
                        </button>
                      )}
                    </td>
                    <td style={s.td}>
                      {p.statusTI === 'Bloqueado' ? (
                        <div style={{color: '#dc2626', fontWeight: 600, fontSize: '0.85rem'}}>
                          🚫 Acesso revogado/bloqueado.
                        </div>
                      ) : p.statusTI === 'Criado' ? (
                        <div style={{background: '#f0fdf4', padding: '12px', border: '1px solid #bbf7d0', borderRadius: '8px', fontSize: '0.85rem'}}>
                          <div style={{color: '#166534', fontWeight: 800, marginBottom: '8px'}}>✔️ ACESSOS ATIVOS</div>
                          <div style={{display: 'grid', gridTemplateColumns: '1fr', gap: '4px', color: '#1e293b'}}>
                            <span><strong>Servidor:</strong> {p.servidorIW || '-'}</span>
                            <span><strong>Login IW:</strong> {p.usuarioSistema}</span>
                            <span><strong>Senha IW:</strong> {p.senhaSistema}</span>
                            <hr style={{margin: '8px 0', border: 'none', borderTop: '1px solid #bbf7d0'}} />
                            <span><strong>Login Aulas:</strong> {p.usuarioAulas}</span>
                            <span><strong>Senha Aulas:</strong> {p.senhaAulas}</span>
                          </div>
                        </div>
                      ) : activeFormId === p.id ? (
                        <div style={{background: '#f8fafc', border: '1px solid #cbd5e1', padding: '16px', borderRadius: '8px'}}>
                          <h4 style={{margin: '0 0 12px 0', fontSize: '0.85rem', color: '#0f172a'}}>Sistema IW</h4>
                          <input style={s.input} placeholder="Servidor IW (ex: IP ou URL)" value={servidorIW} onChange={e=>setServidorIW(e.target.value)} />
                          <div style={{display: 'flex', gap: '8px'}}>
                            <input style={s.input} placeholder="Login IW" value={usuarioIW} onChange={e=>setUsuarioIW(e.target.value)} />
                            <input style={s.input} placeholder="Senha IW" value={senhaIW} onChange={e=>setSenhaIW(e.target.value)} />
                          </div>

                          <h4 style={{margin: '12px 0 12px 0', fontSize: '0.85rem', color: '#0f172a'}}>Plataforma de Aulas</h4>
                          <div style={{display: 'flex', gap: '8px'}}>
                            <input style={s.input} placeholder="Login Aulas" value={usuarioAulas} onChange={e=>setUsuarioAulas(e.target.value)} />
                            <input style={s.input} placeholder="Senha Aulas" value={senhaAulas} onChange={e=>setSenhaAulas(e.target.value)} />
                          </div>

                          <div style={{display: 'flex', gap: '8px', marginTop: '12px'}}>
                            <button style={{...s.btn, background: '#10b981', flex: 1}} onClick={() => handleFinalizarCadastro(p)}>Confirmar Acessos</button>
                            <button style={{...s.btn, background: '#94a3b8'}} onClick={() => setActiveFormId(null)}>Cancelar</button>
                          </div>
                        </div>
                      ) : (
                        <button style={{...s.btn, width: '100%'}} onClick={() => { setActiveFormId(p.id); setUsuarioIW(''); setSenhaIW(''); setServidorIW(''); setUsuarioAulas(''); setSenhaAulas(''); }}>
                          Gerar Acessos
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
                {listaExibida.length === 0 && (
                  <tr><td colSpan={4} style={{...s.td, textAlign: 'center'}}>{activeListTab === 'pendentes' ? 'Nenhum profissional pendente de acesso no momento.' : 'Nenhum acesso concluído ainda.'}</td></tr>
                )}
              </tbody>
            </table>
            </div>
          )
        )}

        {activeTab === 'resets' && (
          <table style={s.table}>
            <thead>
              <tr>
                <th style={s.th}>Profissional</th>
                <th style={s.th}>Cooperativa</th>
                <th style={s.th}>Data Solicitação</th>
                <th style={s.th}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {resets.map(r => (
                <tr key={r.id}>
                  <td style={s.td}>
                    <strong>{r.profNome}</strong><br/>
                    <span style={{fontSize: '0.8rem', color: '#64748b'}}>CPF: {r.profCpf}</span>
                  </td>
                  <td style={s.td}>{r.cooperativaNome}</td>
                  <td style={s.td}>{new Date(r.criadoEm).toLocaleString('pt-BR')}</td>
                  <td style={s.td}>
                    {r.status === 'Pendente' ? (
                      <div style={{display: 'flex', gap: '8px'}}>
                        <button style={{...s.btn, background: '#10b981'}} onClick={() => handleResponderReset(r, 'Finalizado')}>✅ Resetado</button>
                        <button style={{...s.btn, background: '#dc2626'}} onClick={() => handleResponderReset(r, 'Recusado')}>❌ Recusar</button>
                      </div>
                    ) : (
                      <span style={s.badge(r.status === 'Finalizado' ? 'Criado' : 'Bloqueado')}>{r.status}</span>
                    )}
                  </td>
                </tr>
              ))}
              {resets.length === 0 && (
                <tr><td colSpan={4} style={{...s.td, textAlign: 'center'}}>Nenhuma solicitação de reset no momento.</td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Modal de Edição TI */}
      {isEditModalOpen && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.6)', backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 3000 }}>
          <form style={{ background: '#fff', width: '600px', borderRadius: '16px', padding: '32px', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.5)', maxHeight: '90vh', overflowY: 'auto' }} onSubmit={handleSaveEdit}>
            <h2 style={{ margin: '0 0 20px', fontSize: '1.4rem', color: '#0f172a' }}>Editar Ficha do Profissional</h2>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div>
                <label style={{ fontSize: '0.8rem', fontWeight: 700, color: '#64748b' }}>Nome Completo</label>
                <input style={s.input} value={editForm.nome || ''} onChange={e => setEditForm({...editForm, nome: e.target.value})} />
              </div>
              <div>
                <label style={{ fontSize: '0.8rem', fontWeight: 700, color: '#64748b' }}>Especialidade</label>
                <input style={s.input} value={editForm.tipo || ''} onChange={e => setEditForm({...editForm, tipo: e.target.value})} />
              </div>
              <div>
                <label style={{ fontSize: '0.8rem', fontWeight: 700, color: '#64748b' }}>CPF</label>
                <input style={s.input} value={editForm.cpf || ''} onChange={e => setEditForm({...editForm, cpf: e.target.value})} />
              </div>
              <div>
                <label style={{ fontSize: '0.8rem', fontWeight: 700, color: '#64748b' }}>RG</label>
                <input style={s.input} value={editForm.rg || ''} onChange={e => setEditForm({...editForm, rg: e.target.value})} />
              </div>
              <div>
                <label style={{ fontSize: '0.8rem', fontWeight: 700, color: '#64748b' }}>Conselho</label>
                <input style={s.input} value={editForm.conselho || ''} onChange={e => setEditForm({...editForm, conselho: e.target.value})} />
              </div>
              <div>
                <label style={{ fontSize: '0.8rem', fontWeight: 700, color: '#64748b' }}>Registro (CRM/COREN)</label>
                <input style={s.input} value={editForm.crmCoren || ''} onChange={e => setEditForm({...editForm, crmCoren: e.target.value})} />
              </div>
              <div>
                <label style={{ fontSize: '0.8rem', fontWeight: 700, color: '#64748b' }}>Telefone</label>
                <input style={s.input} value={editForm.telefone || ''} onChange={e => setEditForm({...editForm, telefone: e.target.value})} />
              </div>
              <div>
                <label style={{ fontSize: '0.8rem', fontWeight: 700, color: '#64748b' }}>E-mail</label>
                <input style={s.input} value={editForm.emailCooperado || ''} onChange={e => setEditForm({...editForm, emailCooperado: e.target.value})} />
              </div>
            </div>

            <h3 style={{ margin: '20px 0 16px', fontSize: '1rem', color: '#0f172a', borderBottom: '1px solid #e2e8f0', paddingBottom: '8px' }}>Dados de Vínculo e Acesso</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={{ fontSize: '0.8rem', fontWeight: 700, color: '#64748b' }}>Cooperativa</label>
                <input style={s.input} value={editForm.cooperativa || ''} onChange={e => setEditForm({...editForm, cooperativa: e.target.value})} />
              </div>
              <div>
                <label style={{ fontSize: '0.8rem', fontWeight: 700, color: '#64748b' }}>Servidor IW</label>
                <input style={s.input} value={editForm.servidorIW || ''} onChange={e => setEditForm({...editForm, servidorIW: e.target.value})} />
              </div>
              <div></div> {/* Placeholder para manter alinhamento */}
              <div>
                <label style={{ fontSize: '0.8rem', fontWeight: 700, color: '#64748b' }}>Login IW</label>
                <input style={s.input} value={editForm.usuarioSistema || ''} onChange={e => setEditForm({...editForm, usuarioSistema: e.target.value})} />
              </div>
              <div>
                <label style={{ fontSize: '0.8rem', fontWeight: 700, color: '#64748b' }}>Senha IW</label>
                <input style={s.input} value={editForm.senhaSistema || ''} onChange={e => setEditForm({...editForm, senhaSistema: e.target.value})} />
              </div>
              <div>
                <label style={{ fontSize: '0.8rem', fontWeight: 700, color: '#64748b' }}>Login Aulas</label>
                <input style={s.input} value={editForm.usuarioAulas || ''} onChange={e => setEditForm({...editForm, usuarioAulas: e.target.value})} />
              </div>
              <div>
                <label style={{ fontSize: '0.8rem', fontWeight: 700, color: '#64748b' }}>Senha Aulas</label>
                <input style={s.input} value={editForm.senhaAulas || ''} onChange={e => setEditForm({...editForm, senhaAulas: e.target.value})} />
              </div>
            </div>

            <div style={{ display: 'flex', gap: '12px', marginTop: '24px', justifyContent: 'flex-end' }}>
              <button type="button" style={{ ...s.btn, background: '#f1f5f9', color: '#475569' }} onClick={() => setIsEditModalOpen(false)}>Cancelar</button>
              <button type="submit" style={{ ...s.btn, background: '#0f172a' }}>Salvar Alterações</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
